import { useEffect, useState } from 'react';
import {
	HOME_NAV_OPTION,
	TRANSFER_NAV_OPTION,
	INVEST_NAV_OPTION,
	COACH_NAV_OPTION,
	type NavOption,
} from '../constants';
import { useBanjo } from '@stashinvest/react-banjo';
import { track } from 'stash/hooks/useTrack';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { trackOrigin } from 'stash/actions';
import { Origin } from '@stashinvest/product-events-typescript';
import { useIsInExperimentVariation, EXPERIMENTS } from 'stash/components/ab-testing';
import { DEBIT } from 'stash/constants/accountTypes';
import { selectAccounts } from 'stash/selectors';

type UseNavOptionsResult = {
	selectedIndex: number | null | undefined;
	getNavOptions: () => NavOption[];
	getSelectedNavIndex: (pathname: string) => number | null;
	// Optional to stay assignable to TopBar/MobileNav's inferred `() => void` prop type
	onNavLinkClick: (index?: number) => void;
};

const useNavOptions = (): UseNavOptionsResult => {
	const { getString } = useBanjo();
	const { pathname } = useLocation();
	const [selectedIndex, setSelectedIndex] = useState<number | null>();
	const dispatch = useDispatch();
	const accounts = useSelector(selectAccounts) as Record<string, { type?: string }>;
	const hasInvestmentAccount = Object.values(accounts).some(
		(account) => account.type !== DEBIT
	);
	const isCoachTabEnabled = useIsInExperimentVariation(
		EXPERIMENTS.WEB_AI_MONEY_COACH_TAB_ENABLED,
		true
	);

	const updateBanjoStrings = (navOptions: NavOption[]): NavOption[] => {
		return navOptions.map((option) => ({
			...option,
			railOption: {
				...option.railOption,
				title: getString(option.banjoId),
			},
		}));
	};

	const getNavOptions = (): NavOption[] => {
		const navOptions = [HOME_NAV_OPTION, INVEST_NAV_OPTION];
		if (hasInvestmentAccount) {
			navOptions.push(TRANSFER_NAV_OPTION);
			if (isCoachTabEnabled) {
				navOptions.push(COACH_NAV_OPTION);
			}
		}

		return updateBanjoStrings(navOptions);
	};

	const getSelectedNavIndex = (pathname: string): number | null => {
		const navOptions = getNavOptions();
		const index = navOptions?.findIndex((optionKey) => {
			return optionKey.urlMatches.some((url) => {
				return new RegExp(url).test(pathname);
			});
		});

		return index > -1 ? index : null;
	};

	const onNavLinkClick = (index?: number): void => {
		if (typeof index !== 'number' || !Number.isInteger(index)) {
			return;
		}

		// negative index means profile icon or referral link was clicked
		if (index > -1) {
			const navOption = getNavOptions()[index];
			if (!navOption) {
				return;
			}

			dispatch(trackOrigin(Origin?.rail));
			const segmentEvent = navOption.event({
				origin: Origin.rail,
			});
			if (segmentEvent) {
				track(segmentEvent);
			}
		}

		setSelectedIndex(index);
	};

	useEffect(() => {
		setSelectedIndex(getSelectedNavIndex(pathname));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	return {
		selectedIndex,
		getNavOptions,
		getSelectedNavIndex,
		onNavLinkClick,
	};
};

export default useNavOptions;
